/**
 * liquiditaet-dashboard — pure logic for the liquidity dashboard widgets.
 *
 * All functions are side-effect-free so they can be unit-tested without
 * a React or DB setup. The React components consume these as helpers.
 */

import type { FinanzKonto, FinanzTransaktion } from '@/hooks/useLiquiditaet';
import { localIsoDate } from './date-utils';

export const NIEDRIG_SALDO_SCHWELLE = 1000;

export interface FahrzeugTilgungsplanInfo {
  id: string;
  kennzeichen: string | null;
  restschuld: number | null;
  monatliche_rate: number | null;
  laufzeitende: string | null;
  firma: string | null;
}

export function istNiedrigerSaldo(saldo: number): boolean {
  return saldo < NIEDRIG_SALDO_SCHWELLE;
}

export function istBarkasse(typ: string): boolean {
  return typ === 'barkasse';
}

/**
 * Berechnet die Soll-Restschuld zum Stichtag `heute`.
 * Vereinfachtes lineares Modell: Rate × verbleibende Monate bis Laufzeitende.
 */
export function berechneSollRestschuld(
  monatliche_rate: number | null,
  laufzeitende: string | null,
  heute: Date = new Date(),
): number | null {
  if (monatliche_rate === null || laufzeitende === null) return null;

  const endDatum = new Date(laufzeitende);
  if (Number.isNaN(endDatum.getTime())) return null;

  if (endDatum <= heute) return 0;

  const monate = (endDatum.getFullYear() - heute.getFullYear()) * 12
    + (endDatum.getMonth() - heute.getMonth());
  return Math.round(monatliche_rate * Math.max(0, monate));
}

/**
 * Gibt true zurück wenn die tatsächliche Restschuld den Soll-Wert übersteigt.
 */
export function hatTilgungsplanAbweichung(
  info: FahrzeugTilgungsplanInfo,
  heute: Date = new Date(),
): boolean {
  const { restschuld, monatliche_rate, laufzeitende } = info;
  if (restschuld === null) return false;
  const soll = berechneSollRestschuld(monatliche_rate, laufzeitende, heute);
  if (soll === null) return false;
  return restschuld > soll;
}

/**
 * Berechnet den Trend eines Kontos für die letzten 7 Tage basierend auf
 * dem Netto-Cashflow der Transaktionen.
 */
export function berechneTrend7Tage(
  transaktionen: FinanzTransaktion[],
  kontoId: string,
  heute: Date = new Date(),
): 'up' | 'down' | 'neutral' {
  const siebTageVor = new Date(heute.getTime() - 7 * 24 * 60 * 60 * 1000);
  const schwelle = localIsoDate(siebTageVor);

  const netto = transaktionen
    .filter(t => t.konto_id === kontoId && t.datum >= schwelle)
    .reduce((sum, t) => sum + Number(t.betrag), 0);

  if (netto > 0) return 'up';
  if (netto < 0) return 'down';
  return 'neutral';
}

/**
 * Gruppiert eine Liste von Konten nach ihrem `firma`-Schlüssel.
 * Konten ohne Firma landen unter 'sonstige'.
 */
export function gruppiereKontenNachFirma(
  konten: FinanzKonto[],
): Record<string, FinanzKonto[]> {
  return konten.reduce<Record<string, FinanzKonto[]>>((acc, k) => {
    const key = k.firma || 'sonstige';
    if (!acc[key]) acc[key] = [];
    acc[key].push(k);
    return acc;
  }, {});
}
